<template>
  <footer class="pp-footer">
    <!-- Main footer area -->
    <div class="pp-footer__main">
      <div class="pp-footer__inner">
        <div class="pp-footer__grid">
          <!-- Brand + contacts column -->
          <div>
            <router-link to="/" class="pp-footer__logo">
              parcelp<span class="dot">o</span>int
            </router-link>
            <p class="pp-footer__affiliation">
              {{ $t('footer.affiliation_text', "ParcelPoint is an affiliated logistics technology platform used in connection with StockFore Inc. operations to support shipment intake, warehouse workflows, package processing, and tracking coordination.") }}
            </p>
            <div class="pp-footer__address">
              Houston Operations Office<br>
              5373 West Alabama St, Ste 590<br>
              Houston, Texas 77056, United States
            </div>
            <a href="mailto:contact@stockfore.us" class="pp-footer__contact-link">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="2" y="4" width="20" height="16" rx="2"/>
                <path d="m2 7 10 7 10-7"/>
              </svg>
              contact@stockfore.us
            </a>
            <a href="tel:+15127853401" class="pp-footer__contact-link">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.15 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.06 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21 17z"/>
              </svg>
              +1 (512) 785-3401
            </a>
          </div>

          <!-- Platform column -->
          <div>
            <p class="pp-footer__col-title">{{ $t('footer.platform_title', 'Platform') }}</p>
            <ul class="pp-footer__nav">
              <li>
                <router-link :to="`/${$route.params.lang}/track/`">
                  {{ $t('links.track_parcel', 'Track Parcel') }}
                </router-link>
              </li>
              <li>
                <a href="#">
                  {{ $t('links.send_parcel', 'Send Parcel') }}
                </a>
              </li>
              <li>
                <router-link :to="`/${$route.params.lang}/business/`">
                  {{ $t('links.for_business', 'For Business') }}
                </router-link>
              </li>
              <li>
                <a href="#">
                  {{ $t('links.calculate_delivery', 'Calculate Delivery') }}
                </a>
              </li>
            </ul>
          </div>

          <!-- Legal column -->
          <div>
            <p class="pp-footer__col-title">{{ $t('footer.legal_title', 'Legal') }}</p>
            <ul class="pp-footer__nav">
              <li>
                <router-link :to="`/${$route.params.lang}/privacy`">
                  {{ $t('links.privacy_policy', 'Privacy Policy') }}
                </router-link>
              </li>
              <li>
                <router-link :to="`/${$route.params.lang}/terms-of-service/`">
                  {{ $t('links.terms_of_service', 'Terms of Service') }}
                </router-link>
              </li>
              <li>
                <router-link :to="`/${$route.params.lang}/contact`">
                  {{ $t('links.contact', 'Contact') }}
                </router-link>
              </li>
            </ul>
          </div>

          <!-- Social column -->
          <div>
            <p class="pp-footer__col-title">{{ $t('footer.connect_title', 'Connect') }}</p>
            <div class="pp-footer__social">
              <a href="#" @click.prevent="redirectTo('https://t.me/ParcelPoint_bot')" class="pp-footer__social-link">
                <span class="pp-footer__social-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#e63329" stroke-width="2">
                    <line x1="22" y1="2" x2="11" y2="13"/>
                    <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                  </svg>
                </span>
                {{ $t('links.telegram_bot', 'Telegram Bot') }}
              </a>
              <a href="#" @click.prevent="redirectTo('https://example.com')" class="pp-footer__social-link">
                <span class="pp-footer__social-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#e63329" stroke-width="2">
                    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
                  </svg>
                </span>
                Facebook
              </a>
              <a href="#" @click.prevent="redirectTo('https://example.com')" class="pp-footer__social-link">
                <span class="pp-footer__social-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#e63329" stroke-width="2">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                  </svg>
                </span>
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- StockFore affiliation strip -->
    <div class="pp-footer__stockfore-bar">
      <div class="pp-footer__stockfore-inner">
        <p class="pp-footer__stockfore-text">
          {{ $t('footer.stockfore_prefix', 'Corporate logistics operations supported by') }}&nbsp;
          <a href="https://stockfore.us" target="_blank" rel="noopener">StockFore™</a>
          &nbsp;·&nbsp;
          <a href="https://stockfore.us" target="_blank" rel="noopener">stockfore.us</a>
        </p>
      </div>
    </div>

    <!-- Bottom legal bar -->
    <div class="pp-footer__bottom">
      <div class="pp-footer__bottom-inner">
        <span class="pp-footer__copyright">
          © {{ currentYear }} Parcelpoint LLC
        </span>
        <nav class="pp-footer__legal">
          <router-link :to="`/${$route.params.lang}/privacy`">
            {{ $t('links.privacy_policy', 'Privacy Policy') }}
          </router-link>
          <router-link :to="`/${$route.params.lang}/terms-of-service/`">
            {{ $t('links.terms_of_service', 'Terms of Service') }}
          </router-link>
          <router-link :to="`/${$route.params.lang}/contact`">
            {{ $t('links.contact', 'Contact') }}
          </router-link>
        </nav>
      </div>
    </div>
  </footer>
</template>

<script>
export default {
  name: 'FooterMain',
  computed: {
    currentYear() {
      return new Date().getFullYear()
    }
  },
  methods: {
    redirectTo(link) {
      window.open(link, '_blank')
    }
  }
}
</script>

<style scoped>
/* Reset and base */
*,
*::before,
*::after {
  box-sizing: border-box;
}

.pp-footer {
  font-family: 'Outfit', 'Helvetica Neue', Arial, sans-serif;
  background: #111111;
}

/* Main footer area */
.pp-footer__main {
  background: #111111;
  padding: 52px 0 44px;
}

.pp-footer__inner {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 40px;
}

.pp-footer__grid {
  display: grid;
  grid-template-columns: 1.8fr 1fr 1fr 1fr;
  gap: 48px;
  align-items: start;
}

/* Brand column */
.pp-footer__logo {
  display: inline-block;
  font-size: 22px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.03em;
  text-decoration: none;
  margin-bottom: 16px;
}

.pp-footer__logo .dot {
  color: #e63329;
}

.pp-footer__affiliation {
  font-size: 13px;
  line-height: 1.65;
  color: #666;
  max-width: 320px;
  margin-bottom: 22px;
}

.pp-footer__address {
  font-size: 13px;
  line-height: 1.8;
  color: #555;
  margin-bottom: 16px;
}

.pp-footer__contact-link {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #555;
  text-decoration: none;
  margin-bottom: 7px;
  transition: color 0.15s;
}

.pp-footer__contact-link:hover {
  color: #fff;
}

.pp-footer__contact-link svg {
  color: #e63329;
  flex-shrink: 0;
}

/* Navigation columns */
.pp-footer__col-title {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #fff;
  margin-bottom: 18px;
}

.pp-footer__nav {
  list-style: none;
  padding: 0;
}

.pp-footer__nav li {
  margin-bottom: 12px;
}

.pp-footer__nav a {
  font-size: 14px;
  font-weight: 400;
  color: #666;
  text-decoration: none;
  transition: color 0.15s;
}

.pp-footer__nav a:hover {
  color: #fff;
}

/* Social column */
.pp-footer__social {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.pp-footer__social-link {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #666;
  text-decoration: none;
  transition: color 0.15s;
}

.pp-footer__social-link:hover {
  color: #fff;
}

.pp-footer__social-icon {
  width: 32px;
  height: 32px;
  background: #1e1e1e;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* StockFore bar */
.pp-footer__stockfore-bar {
  background: #111111;
  padding: 16px 0;
  border-top: 1px solid #1e1e1e;
}

.pp-footer__stockfore-inner {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pp-footer__stockfore-text {
  font-size: 13px;
  color: #444;
}

.pp-footer__stockfore-text a {
  color: #e63329;
  font-weight: 600;
  text-decoration: none;
}

.pp-footer__stockfore-text a:hover {
  text-decoration: underline;
}

/* Bottom bar */
.pp-footer__bottom {
  background: #0a0a0a;
  padding: 18px 0;
}

.pp-footer__bottom-inner {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

.pp-footer__copyright {
  font-size: 13px;
  color: #444;
}

.pp-footer__legal {
  display: flex;
  align-items: center;
  gap: 0;
}

.pp-footer__legal a,
.pp-footer__legal .router-link-active,
.pp-footer__legal .router-link-exact-active {
  font-size: 13px;
  color: #444;
  text-decoration: none;
  padding: 0 16px;
  border-right: 1px solid #333;
  transition: color 0.15s;
}

.pp-footer__legal a:last-child {
  border-right: none;
}

.pp-footer__legal a:hover {
  color: #fff;
}

/* Responsive */
@media (max-width: 900px) {
  .pp-footer__grid {
    grid-template-columns: 1fr 1fr;
    gap: 32px;
  }
}

@media (max-width: 600px) {
  .pp-footer__grid {
    grid-template-columns: 1fr;
  }
  .pp-footer__bottom-inner {
    flex-direction: column;
    align-items: flex-start;
  }
  .pp-footer__inner {
    padding: 0 20px;
  }
  .pp-footer__stockfore-inner {
    padding: 0 20px;
  }
  .pp-footer__bottom-inner {
    padding: 0 20px;
  }
  .pp-footer__legal a {
    padding: 0 12px;
  }
}
</style>